/**
 * Wreck pool slots and wreck hull bodies (M2-17, plan §6 M2-17). Combat, death and contract wrecks carry a
 * kinematic, velocity-driven Rapier body built from the ship def's collider (`sim.shipColliders[defIndex]`, else a
 * ball of the def's collider radius), mapped to `EID_WRECK` and slowed by `salvage.json wrecks.drag`. Derelict
 * sections have no body of their own: they use the Harrow's static collider and pose.
 *
 * Wreck hulls collide with ships only (not with loose debris): freed parts spawn on the hull and the tractor pulls
 * them through it, so a debris contact would only fling them. Bodies are built and removed on rare events (a kill,
 * a death, a contract, a load); `syncWreckBodies` runs every tick and is allocation-free (D-014). Float64 world
 * positions reach Rapier only through the physics origin (D-011, D-013).
 */
import RAPIER from '@dimforge/rapier3d-compat'
import type { RigidBody } from '@dimforge/rapier3d-compat'
import type { ColliderData } from '../assets/extract'
import type { Quat } from '../core/math'
import { EID_WRECK, GROUP_SHIPS, GROUP_STATIC, type Physics } from '../physics/physics'
import type { WreckPool } from './contracts'

/** Wreck hulls: static membership (ships treat them like the derelict), colliding with ships only. */
export const WRECK_COLLISION_GROUPS = (GROUP_STATIC << 16) | GROUP_SHIPS

/** Takes a free wreck slot (lowest first), marks it alive and returns it; -1 when the pool is full. */
export function allocWreckSlot(pool: WreckPool): number {
  for (let w = 0; w < pool.capacity; w++) {
    if (pool.alive[w] !== 0) continue
    pool.alive[w] = 1
    pool.live++
    if (w + 1 > pool.high) pool.high = w + 1
    pool.looted[w] = 0
    pool.lootChecked[w] = 0
    pool.partCount[w] = 0
    pool.section[w] = -1
    pool.defIndex[w] = -1
    pool.contractId[w] = null
    pool.key[w] = ''
    return w
  }
  return -1
}

/** Frees wreck slot `w` and shrinks `high` past trailing dead slots. */
export function releaseWreckSlot(pool: WreckPool, w: number): void {
  if (pool.alive[w] === 0) return
  pool.alive[w] = 0
  pool.live--
  pool.contractId[w] = null
  pool.key[w] = ''
  while (pool.high > 0 && pool.alive[pool.high - 1] === 0) pool.high--
}

/** Writes a wreck's float64 pose and velocity into slot `w`. */
export function setWreckPose(
  pool: WreckPool,
  w: number,
  x: number,
  y: number,
  z: number,
  q: Readonly<Quat>,
  vx: number,
  vy: number,
  vz: number,
): void {
  pool.x[w] = x
  pool.y[w] = y
  pool.z[w] = z
  pool.qx[w] = q.x
  pool.qy[w] = q.y
  pool.qz[w] = q.z
  pool.qw[w] = q.w
  pool.vx[w] = vx
  pool.vy[w] = vy
  pool.vz[w] = vz
}

/**
 * A kinematic (velocity-driven) hull body at the float64 pose of wreck slot `w`, with one collider per convex hull
 * of `collider` (a ball of `fallbackRadius` when there is none, or for a ball or trimesh collider), every collider
 * mapped to `EID_WRECK`. A rare event: it allocates inside Rapier's binding (D-024).
 */
export function createWreckBody(p: Physics, pool: WreckPool, w: number, collider: ColliderData | null, fallbackRadius: number): RigidBody {
  const desc = RAPIER.RigidBodyDesc.kinematicVelocityBased()
    .setTranslation(pool.x[w] - p.origin.x, pool.y[w] - p.origin.y, pool.z[w] - p.origin.z)
    .setRotation({ x: pool.qx[w], y: pool.qy[w], z: pool.qz[w], w: pool.qw[w] })
    .setLinvel(pool.vx[w], pool.vy[w], pool.vz[w])
  const body = p.world.createRigidBody(desc)
  let built = 0
  if (collider?.kind === 'convexHulls') {
    for (let i = 0; i < collider.hulls.length; i++) {
      const shape = RAPIER.ColliderDesc.convexHull(collider.hulls[i])
      if (!shape) continue
      try {
        const handle = p.world.createCollider(shape.setCollisionGroups(WRECK_COLLISION_GROUPS), body).handle
        p.colliderEid.set(handle, EID_WRECK)
        built++
      } catch {
        // A degenerate hull: the wreck keeps its other hulls (or falls back to the ball below).
      }
    }
  }
  if (built === 0) {
    const radius = collider?.kind === 'ball' ? collider.radius : fallbackRadius
    const handle = p.world.createCollider(RAPIER.ColliderDesc.ball(radius).setCollisionGroups(WRECK_COLLISION_GROUPS), body).handle
    p.colliderEid.set(handle, EID_WRECK)
  }
  return body
}

/** Removes a wreck body, its colliders and their eid mappings. */
export function removeWreckBody(p: Physics, body: RigidBody): void {
  const n = body.numColliders()
  for (let i = 0; i < n; i++) p.colliderEid.delete(body.collider(i).handle)
  p.world.removeRigidBody(body)
}

const tv = { x: 0, y: 0, z: 0 }

/**
 * Per tick after the Rapier step: reads every wreck body back into float64 world positions, then slows it by
 * `drag` (per second) and hands the new velocity to the body for the next step. Wrecks keep their rotation.
 * Allocation-free.
 */
export function syncWreckBodies(p: Physics, pool: WreckPool, bodies: (RigidBody | undefined)[], drag: number, dt: number): void {
  let k = 1 - drag * dt
  if (k < 0) k = 0
  for (let w = 0; w < pool.high; w++) {
    if (pool.alive[w] === 0) continue
    const body = bodies[w]
    if (body === undefined) continue
    body.translation(tv)
    pool.x[w] = p.origin.x + tv.x
    pool.y[w] = p.origin.y + tv.y
    pool.z[w] = p.origin.z + tv.z
    const vx = pool.vx[w] * k
    const vy = pool.vy[w] * k
    const vz = pool.vz[w] * k
    pool.vx[w] = vx
    pool.vy[w] = vy
    pool.vz[w] = vz
    tv.x = vx
    tv.y = vy
    tv.z = vz
    body.setLinvel(tv, true)
  }
}
